import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Radio, Users, Calendar, ArrowUpRight, ExternalLink,
  Twitch, Youtube, Clapperboard, Gamepad2, Brush, MessageSquare,
  TrendingUp, ChevronRight
} from 'lucide-react';
import PageTransition from '../../components/common/PageTransition';
import SEOHead from '../../components/common/SEOHead';
import HeroBanner from '../../components/common/HeroBanner';
import { streamSchedule } from '../../data/streamSchedule';
import { heroStreamSocials, getSocialById } from '../../data/socialLinks';
import SocialIcon from '../../components/common/SocialIcon';
import { channelStats, formatCount } from '../../data/channelStats';

// ─── Schedule icon helper ───────────────────────────────────────────────────
const scheduleTypeIcon = (type: string) => {
  if (type.toLowerCase().includes('gaming') || type.toLowerCase().includes('jeu'))
    return <Gamepad2 className="w-4 h-4" />;
  if (type.toLowerCase().includes('art') || type.toLowerCase().includes('code'))
    return <Brush className="w-4 h-4" />;
  if (type.toLowerCase().includes('chat'))
    return <MessageSquare className="w-4 h-4" />;
  return <Radio className="w-4 h-4" />;
};



// ─── Stat Card ──────────────────────────────────────────────────────────────
const StatCard: React.FC<{
  stat: typeof channelStats[0];
}> = ({ stat }) => {
  const { t } = useTranslation();
  return (
    <a
      href={stat.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center gap-4 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50 hover:scale-[1.03] transition-all duration-300 group opacity-70`}
    >
      <div className={`w-10 h-10 rounded-xl bg-slate-200 dark:bg-slate-800 flex items-center justify-center shrink-0 grayscale`}>
        <img src={stat.icon} alt={stat.platform} className="w-5 h-5 opacity-50" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 truncate">
          @{stat.handle}
        </p>
        <p className={`text-2xl font-extrabold font-heading tracking-tight text-slate-400 dark:text-slate-500`}>
          —
        </p>
        <p className="text-xs text-slate-500 dark:text-slate-400">
          {t(`stream.stats.${stat.id}.label`, { defaultValue: t('stream.stats.followers') })}
        </p>
      </div>
      <ExternalLink className="w-4 h-4 text-slate-400 group-hover:text-slate-200 transition-colors shrink-0" />
    </a>
  );
};

const FeaturedPlatformCard: React.FC<{
  href: string;
  icon: string;
  label: string;
  sub: string;
}> = ({ href, icon, label, sub }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className="flex flex-col sm:flex-row sm:items-center gap-5 p-6 rounded-2xl art-card border-white/5 hover:border-red-500/30 hover:scale-[1.02] transition-all group relative overflow-hidden"
  >
    <div className={`absolute top-0 right-0 w-40 h-40 bg-red-500/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 group-hover:bg-red-500/10 transition-colors duration-500`}></div>
    <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center shrink-0">
      <img src={icon} alt={label} className="w-7 h-7" />
    </div>
    <div className="flex-1 min-w-0 relative z-10">
      <p className="font-bold text-slate-900 dark:text-white text-lg">{label}</p>
      <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{sub}</p>
    </div>
    <ArrowUpRight className="w-6 h-6 text-slate-400 group-hover:text-red-500 transition-colors shrink-0 relative z-10 self-end sm:self-auto" />
  </a>
);

// ─── Main component ─────────────────────────────────────────────────────────
const StreamHome: React.FC = () => {
  const { t } = useTranslation();

  return (
    <PageTransition>
      <SEOHead title={t('nav.stream')} />
      <div className="space-y-12">

        {/* ── Hero ── */}
        <HeroBanner
          title={t('stream.home.title')}
          subtitle={t('home.streamCard.subtitle')}
          variant="stream"
          ctaText={t('stream.home.watchOnTwitch')}
          ctaLink="https://www.twitch.tv/kynothan"
        />

        {/* ── Social icons ── */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          {heroStreamSocials.map(id => {
            const social = getSocialById(id);
            return social ? <SocialIcon key={social.id} social={social} size="lg" /> : null;
          })}
        </div>

        {/* ── Live Status + Stats ── */}
        <section className="space-y-4">
          {/* Status banner */}
          <div className="rounded-2xl bg-gradient-to-r from-brand-purple/10 via-black/0 to-transparent border border-brand-purple/20 p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="relative flex h-5 w-5 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-purple opacity-60" />
                <span className="relative inline-flex rounded-full h-5 w-5 bg-brand-purple" />
              </div>
              <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('stream.home.offlineSubtitle')}
                </p>
              </div>
            </div>
            <a
              href="https://www.twitch.tv/kynothan"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-brand-purple hover:bg-purple-500 text-white font-bold transition-all hover:scale-[1.02] shrink-0 shadow-lg shadow-brand-purple/20 w-full sm:w-auto justify-center"
            >
              <img src="/img/twitch.svg" alt="Twitch" className="w-4 h-4" />
              {t('stream.home.goToTwitch')}
            </a>
          </div>

          {/* Channel stats */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-5 h-5 text-brand-purple" />
              <h3 className="text-sm font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                {t('stream.stats.title')}
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {channelStats.map(stat => (
                <StatCard key={stat.id} stat={stat} />
              ))}
            </div>
            <div className="flex items-center justify-end mt-2">
              <a
                href="/contact"
                className="text-xs font-semibold text-brand-purple hover:underline hover:text-purple-400 transition-colors"
              >
                {t('stream.stats.mediaKit')}
              </a>
            </div>
          </div>
        </section>

        {/* ── Content Platforms ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Clapperboard className="w-6 h-6 text-brand-purple" />
            <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
              {t('stream.content.title')}
            </h2>
          </div>
          <FeaturedPlatformCard
            href="https://www.youtube.com/@kynothanvod"
            icon="/img/youtube.svg"
            label="YouTube"
            sub={t('stream.content.youtube')}
          />
        </section>

        {/* ── Replays ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Youtube className="w-6 h-6 text-red-500" />
            <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
              {t('stream.replays.title', { defaultValue: 'Stream Replays' })}
            </h2>
          </div>
          <FeaturedPlatformCard
            href="https://www.youtube.com/@KynothanReplay"
            icon="/img/youtube.svg"
            label="YouTube Replay"
            sub={t('stream.replays.youtube', { defaultValue: 'Full stream replays' })}
          />
        </section>

        {/* ── Favorite Videos ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Clapperboard className="w-6 h-6 text-brand-purple" />
            <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
              {t('stream.favorites.title', { defaultValue: 'Your favorite videos' })}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              {
                url: 'https://www.tiktok.com/@kynothanvod/video/7580322930723196193',
                platform: 'TikTok',
                title: 'Mundo tank',
                icon: '/img/tiktok.svg',
                thumbnail: '/img/thumbnail/2026.08.06.16.07.57_1500_chrome_[541-760].png'
              },
              {
                url: 'https://www.tiktok.com/@kynothanvod/video/7592319812055895318',
                platform: 'TikTok',
                title: 'Rick le technicien de la porte',
                icon: '/img/tiktok.svg',
                thumbnail: '/img/thumbnail/2026.08.06.16.09.46_1501_chrome_[442-786].png'
              },
              {
                url: 'https://youtu.be/-DqBgVQV5q8',
                platform: 'YouTube',
                title: 'ON ESSAIE DE GÉRER UN RESTAURANT… C’EST LE CHAOS (PlateUp!)',
                icon: '/img/youtube.svg',
                thumbnail: '/img/thumbnail/minia plateup.png'
              },
              {
                url: 'https://www.instagram.com/reel/DXHvVPmDCtS/',
                platform: 'Instagram',
                title: 'Silksong arrêtez tout',
                icon: '/img/instagram.svg',
                thumbnail: '/img/thumbnail/2026.08.06.16.14.04_1502_chrome_[521-900].png'
              },
              {
                url: 'https://www.twitch.tv/kynothan/clip/AdventurousSpikyChickenShadyLulu-qcaKBUkCjTbZHaZ5',
                platform: 'Twitch',
                title: 'Cuisse de grenouille',
                icon: '/img/twitch.svg',
                thumbnail: '/img/thumbnail/2026.08.06.16.15.31_1503_chrome_[1277-672].png'
              }
            ].map((video, idx) => (
              <a key={idx} href={video.url} target="_blank" rel="noopener noreferrer" className="art-card rounded-2xl overflow-hidden group cursor-pointer border-white/5 hover:border-brand-purple/30 transition-all hover:scale-[1.02] block">
                <div className="aspect-video bg-slate-200 dark:bg-slate-800 relative flex items-center justify-center overflow-hidden">
                  {video.thumbnail ? (
                    <img src={video.thumbnail} alt={video.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <img src={video.icon} alt={video.platform} className="w-12 h-12 opacity-30 group-hover:opacity-10 transition-opacity z-10" />
                  )}
                  <div className="absolute inset-0 bg-black/10 group-hover:bg-black/30 transition-colors z-10" />
                  <div className="absolute w-12 h-12 rounded-full bg-brand-purple/80 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all z-20 shadow-lg">
                    <div className="w-0 h-0 border-t-[8px] border-t-transparent border-l-[12px] border-l-white border-b-[8px] border-b-transparent ml-1" />
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="font-bold text-slate-900 dark:text-white text-sm line-clamp-2">
                    {video.title}
                  </h3>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 flex items-center gap-1.5">
                    <img src={video.icon} alt={video.platform} className="w-3 h-3 grayscale" />
                    {video.platform}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </section>

        {/* ── Schedule ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-6 h-6 text-brand-purple" />
            <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
              {t('stream.home.schedule')}
            </h2>
          </div>
          <div className="art-card rounded-2xl overflow-hidden divide-y divide-black/5 dark:divide-white/10">
            {streamSchedule.map((entry, idx) => (
              <div
                key={idx}
                className={`px-5 py-3.5 flex flex-col sm:flex-row sm:items-center justify-between gap-2 transition-colors ${entry.active
                    ? 'hover:bg-brand-purple/5'
                    : 'opacity-40'
                  }`}
              >
                <div className="font-bold text-slate-900 dark:text-white min-w-[110px] text-sm">
                  {t(`stream.schedule.${entry.dayKey.toLowerCase()}`, { defaultValue: entry.dayKey })}
                </div>
                <div className="text-slate-500 dark:text-slate-400 font-mono text-sm min-w-[60px]">
                  {entry.time}
                </div>
                {entry.active ? (
                  <div className="flex items-center gap-1.5 text-brand-purple font-semibold text-sm">
                    {scheduleTypeIcon(entry.type)}
                    {entry.type}
                  </div>
                ) : (
                  <div className="text-slate-400 text-sm">—</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ── Community / Discord ── */}
        <section className="space-y-4">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-indigo-400" />
            <h2 className="text-2xl font-bold font-heading text-slate-900 dark:text-white">
              {t('stream.home.community')}
            </h2>
          </div>

          <div className="art-card rounded-2xl p-6 md:p-8 space-y-4 border-indigo-500/20 text-center">
            <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mx-auto mb-2">
              <img src="/img/discord.svg" alt="Discord" className="w-8 h-8" />
            </div>

            <h3 className="font-bold text-xl text-slate-900 dark:text-white">
              {t('stream.community.discordTitle')}
            </h3>

            <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed max-w-xl mx-auto">
              {t('stream.community.discordDesc')}
            </p>

            <div className="pt-2">
              <a
                href="https://discord.gg/k6yEUBAvWw"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-indigo-500 hover:bg-indigo-400 text-white font-bold text-sm transition-all hover:scale-[1.02] shadow-lg shadow-indigo-500/20"
              >
                <img src="/img/discord.svg" alt="Discord" className="w-5 h-5" />
                {t('stream.community.joinDiscord')}
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* ── Pro / Collabs ── */}
        <section className="border border-brand-purple/20 rounded-2xl overflow-hidden">
          <a
            href="/contact?type=content"
            className="flex items-center justify-between group p-5 hover:bg-brand-purple/5 transition-colors"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-xl bg-brand-purple/10 flex items-center justify-center shrink-0">
                <TrendingUp className="w-5 h-5 text-brand-purple" />
              </div>
              <div>
                <h4 className="font-bold text-slate-900 dark:text-white group-hover:text-brand-purple transition-colors">
                  {t('stream.collab.title')}
                </h4>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {t('stream.collab.subtitle')}
                </p>
              </div>
            </div>
            <ArrowUpRight className="w-5 h-5 text-slate-400 group-hover:text-brand-purple transition-colors shrink-0" />
          </a>
        </section>

      </div>
    </PageTransition>
  );
};

export default StreamHome;


